import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { STUDIO_COPY } from '../config';
import {
  playlistDuration,
  playlistSeekTarget,
  playlistTimeFromSource,
} from './customizeHelpers';

export type ClipSegment = {
  id: string;
  label: string;
  startSec: number;
  endSec: number;
  /** Bumps on each Preview click so trim nudges don't auto-replay. */
  playKey: number;
};

export type ClipSegmentPlayerHandle = {
  /** Seek + play from a user gesture (single-click Preview). */
  playNow: (segment: ClipSegment) => void;
  /** Seek the virtual reel (playlist) or clip-local clock without restarting. */
  seekToReelTime: (sec: number) => void;
  /** Stage <video> for V2 record-to-picture. */
  getVideo: () => HTMLVideoElement | null;
};

type ClipSegmentPlayerProps = {
  sourceUrl: string | null | undefined;
  sourceExpiresAt?: string | null;
  segment: ClipSegment | null;
  /**
   * When set with length > 0, play each segment in order and auto-advance
   * at segment end (virtual reel). Uses the same video element as clip preview.
   */
  playlist?: ClipSegment[] | null;
  /** Bump to (re)start the playlist from the first clip. */
  playlistPlayKey?: number;
  /** Remint source signed URL when expired/missing. */
  onRefreshSource?: () => void | Promise<void>;
  refreshingSource?: boolean;
  testId?: string;
  /** When false, parent already shows the idle hint nearby (avoids duplicate copy). */
  showIdleHint?: boolean;
  /** Optional header title override (e.g. arrangement instant). */
  title?: string | null;
  subtitle?: string | null;
  /**
   * Optional music bed under playlist mode (blob: or same-origin URL).
   * Loops for the virtual reel length; volumes are linear 0–1.
   */
  musicUrl?: string | null;
  videoVolume?: number;
  musicVolume?: number;
  /** Instant preview: loop bed to fill arrangement (default true). */
  musicLoop?: boolean;
  /** Arrangement clock callback for the shared V2 playhead. */
  onPlaybackTime?: (sec: number, duration: number) => void;
  /** Added to clip-local time when reporting a reel-wide clock. */
  reelClockOffsetSec?: number;
  /** Override reported duration (arrangement total). */
  reelClockDurationSec?: number;
};

function isExpired(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return false;
  return Date.now() >= ms;
}

function formatSec(sec: number): string {
  const s = Math.max(0, sec);
  const m = Math.floor(s / 60);
  const r = (s % 60).toFixed(1);
  return m > 0 ? `${m}:${r.padStart(4, '0')}` : `${Number(r).toFixed(1)}s`;
}

/**
 * Shared Customize/Refine per-clip player: seek retained source to start→end
 * and hard-stop at endSec (source is the full VOD). Playlist mode sequences
 * multiple segments for an instant adjusted-reel preview.
 *
 * Video stays mounted when source is available so the first Preview click can
 * play under the user-gesture (via playNow / playKey effect).
 */
const ClipSegmentPlayer = forwardRef<
  ClipSegmentPlayerHandle,
  ClipSegmentPlayerProps
>(function ClipSegmentPlayer(
  {
    sourceUrl,
    sourceExpiresAt = null,
    segment,
    playlist = null,
    playlistPlayKey = 0,
    onRefreshSource,
    refreshingSource = false,
    testId = 'studio-clip-segment-player',
    showIdleHint = true,
    title = null,
    subtitle = null,
    musicUrl = null,
    videoVolume,
    musicVolume,
    musicLoop = true,
    onPlaybackTime,
    reelClockOffsetSec = 0,
    reelClockDurationSec,
  },
  ref
) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const endSecRef = useRef(0);
  const playlistRef = useRef<ClipSegment[]>([]);
  const indexRef = useRef(0);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const onPlaybackTimeRef = useRef(onPlaybackTime);
  const reelClockOffsetRef = useRef(reelClockOffsetSec);
  const reelClockDurationRef = useRef(reelClockDurationSec);
  onPlaybackTimeRef.current = onPlaybackTime;
  reelClockOffsetRef.current = reelClockOffsetSec;
  reelClockDurationRef.current = reelClockDurationSec;

  const expired = isExpired(sourceExpiresAt);
  const available = Boolean(sourceUrl) && !expired;
  const playlistMode = Boolean(playlist && playlist.length > 0);
  const musicActive = playlistMode && Boolean(musicUrl);

  const syncMusicPlayback = (playing: boolean) => {
    const audio = musicRef.current;
    if (!audio || !musicUrl) return;
    if (playing) {
      void audio.play().catch(() => {
        // Autoplay may be blocked until a user gesture — playlist starts from one.
      });
    } else {
      audio.pause();
    }
  };

  const restartMusic = () => {
    const audio = musicRef.current;
    if (!audio || !musicUrl) return;
    try {
      audio.currentTime = 0;
    } catch {
      // ignore
    }
    void audio.play().catch(() => undefined);
  };

  useEffect(() => {
    playlistRef.current = playlist ?? [];
  }, [playlist]);

  const seekAndPlay = (start: number, end: number) => {
    const video = videoRef.current;
    if (!video || !available) return;
    endSecRef.current = end;
    setLoadError(null);
    setBuffering(true);
    const playFrom = () => {
      try {
        video.currentTime = Math.max(0, start);
      } catch {
        // Ignore until metadata ready.
      }
      void video.play().catch(() => {
        setBuffering(false);
        setLoadError("Couldn't load preview — refresh and try again.");
      });
      if (playlistMode && musicUrl) {
        // Keep bed rolling across clip advances; only restart at playlist start.
        syncMusicPlayback(true);
      }
    };
    if (video.readyState >= 1) {
      playFrom();
    } else {
      const onMeta = () => {
        video.removeEventListener('loadedmetadata', onMeta);
        playFrom();
      };
      video.addEventListener('loadedmetadata', onMeta);
    }
  };

  const playPlaylistIndex = (index: number) => {
    const list = playlistRef.current;
    if (!list.length) return;
    const clamped = Math.max(0, Math.min(index, list.length - 1));
    indexRef.current = clamped;
    setPlaylistIndex(clamped);
    const next = list[clamped];
    if (!next) return;
    seekAndPlay(next.startSec, next.endSec);
  };

  const reportPlaybackTime = () => {
    const notify = onPlaybackTimeRef.current;
    const video = videoRef.current;
    if (!notify || !video) return;
    if (playlistMode) {
      const list = playlistRef.current;
      notify(
        playlistTimeFromSource(list, indexRef.current, video.currentTime),
        playlistDuration(list)
      );
      return;
    }
    if (!segment) return;
    const local = Math.max(0, video.currentTime - segment.startSec);
    const clipDur = Math.max(0, segment.endSec - segment.startSec);
    const offset = reelClockOffsetRef.current;
    const clockDur = reelClockDurationRef.current;
    notify(offset + Math.min(local, clipDur), clockDur && clockDur > 0 ? clockDur : clipDur);
  };

  useImperativeHandle(ref, () => ({
    getVideo: () => videoRef.current,
    playNow: (next) => {
      endSecRef.current = next.endSec;
      seekAndPlay(next.startSec, next.endSec);
    },
    seekToReelTime: (sec) => {
      const video = videoRef.current;
      if (!video || !available) return;
      const wasPlaying = !video.paused && !video.ended;
      if (playlistMode) {
        const target = playlistSeekTarget(playlistRef.current, sec);
        if (!target) return;
        const next = playlistRef.current[target.index];
        if (!next) return;
        indexRef.current = target.index;
        setPlaylistIndex(target.index);
        endSecRef.current = next.endSec;
        try {
          video.currentTime = target.sourceTime;
        } catch {
          // ignore
        }
        if (wasPlaying) {
          void video.play().catch(() => undefined);
        }
        reportPlaybackTime();
        return;
      }
      if (!segment) return;
      const local = sec - reelClockOffsetRef.current;
      const source = Math.max(
        segment.startSec,
        Math.min(segment.endSec, segment.startSec + local)
      );
      endSecRef.current = segment.endSec;
      try {
        video.currentTime = source;
      } catch {
        // ignore
      }
      if (wasPlaying) {
        void video.play().catch(() => undefined);
      }
      reportPlaybackTime();
    },
  }));

  // Single-clip: drive play when Preview arms a new playKey (not on trim nudges alone).
  useEffect(() => {
    if (playlistMode) return;
    if (!segment || !available || !sourceUrl) return;
    const video = videoRef.current;
    if (video && video.src !== sourceUrl) {
      video.src = sourceUrl;
    }
    const frame = requestAnimationFrame(() => {
      seekAndPlay(segment.startSec, segment.endSec);
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- playKey + id gate replay
  }, [segment?.playKey, segment?.id, sourceUrl, available, playlistMode]);

  // Playlist: (re)start from clip 0 when playKey bumps or source becomes ready.
  useEffect(() => {
    if (!playlistMode || !available || !sourceUrl) return;
    const video = videoRef.current;
    if (video && video.src !== sourceUrl) {
      video.src = sourceUrl;
    }
    const frame = requestAnimationFrame(() => {
      if (musicUrl) restartMusic();
      playPlaylistIndex(0);
    });
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- playKey gates restart
  }, [playlistPlayKey, playlistMode, sourceUrl, available, musicUrl]);

  // Linear volumes for instant mix approximation (playlist + optional bed).
  useEffect(() => {
    const video = videoRef.current;
    if (video && typeof videoVolume === 'number') {
      video.volume = Math.max(0, Math.min(1, videoVolume));
    }
  }, [videoVolume, available, playlistMode]);

  useEffect(() => {
    const audio = musicRef.current;
    if (!audio) return;
    if (typeof musicVolume === 'number') {
      audio.volume = Math.max(0, Math.min(1, musicVolume));
    }
    if (!musicActive) {
      audio.pause();
    }
  }, [musicVolume, musicActive, musicUrl]);

  // Keep hard-stop / index in sync when the playlist is reordered or trimmed.
  useEffect(() => {
    if (!playlistMode) {
      if (!segment) return;
      endSecRef.current = segment.endSec;
      return;
    }
    const list = playlist ?? [];
    playlistRef.current = list;
    if (!list.length) return;
    const currentId = list[indexRef.current]?.id;
    let nextIndex = indexRef.current;
    if (currentId) {
      const found = list.findIndex((s) => s.id === currentId);
      nextIndex = found >= 0 ? found : Math.min(indexRef.current, list.length - 1);
    } else {
      nextIndex = 0;
    }
    indexRef.current = nextIndex;
    setPlaylistIndex(nextIndex);
    const current = list[nextIndex];
    if (current) endSecRef.current = current.endSec;
  }, [segment?.endSec, segment?.id, playlist, playlistMode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      reportPlaybackTime();
      if (video.currentTime < endSecRef.current - 0.05) return;

      if (playlistMode) {
        const list = playlistRef.current;
        const nextIndex = indexRef.current + 1;
        if (nextIndex < list.length) {
          playPlaylistIndex(nextIndex);
          return;
        }
        video.pause();
        syncMusicPlayback(false);
        try {
          video.currentTime = endSecRef.current;
        } catch {
          // ignore
        }
        return;
      }

      if (!segment) return;
      video.pause();
      try {
        video.currentTime = endSecRef.current;
      } catch {
        // ignore
      }
    };
    const onWaiting = () => setBuffering(true);
    const onPlaying = () => setBuffering(false);
    const onSeeked = () => setBuffering(false);
    const onError = () => {
      setBuffering(false);
      setLoadError("Couldn't load preview — refresh and try again.");
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
    };
  }, [segment?.id, segment, playlistMode]);

  const active = playlistMode
    ? playlist?.[playlistIndex] ?? null
    : segment;

  if (!available) {
    return (
      <div
        className="studio-customize-player-card rounded-lg border border-white/10 bg-black/40 px-4 py-6 space-y-3 text-center"
        data-testid={testId}
        data-state={active || playlistMode ? 'unavailable' : 'idle'}
      >
        <p className="text-sm text-[var(--studio-muted)]">
          {active || playlistMode
            ? "Couldn't load preview — refresh and try again."
            : showIdleHint
              ? STUDIO_COPY.customizeClipPlayHint
              : 'Preview unavailable — refresh the source link.'}
        </p>
        {onRefreshSource && (active || playlistMode) ? (
          <button
            type="button"
            className="studio-btn-ghost text-xs"
            disabled={refreshingSource}
            onClick={() => {
              void onRefreshSource();
            }}
            data-testid={`${testId}-refresh`}
          >
            {refreshingSource ? 'Refreshing…' : 'Refresh preview'}
          </button>
        ) : null}
      </div>
    );
  }

  const duration = active
    ? Math.max(0, active.endSec - active.startSec)
    : 0;

  return (
    <div
      className="studio-customize-player-card rounded-lg overflow-hidden border border-white/10 bg-black space-y-2"
      data-testid={testId}
      data-state={active ? 'ready' : 'idle'}
      data-playlist={playlistMode ? 'true' : 'false'}
      data-playlist-index={playlistMode ? String(playlistIndex) : undefined}
    >
      {active ? (
        <div className="px-3 pt-3 flex flex-wrap items-baseline justify-between gap-2">
          <p
            className="text-sm font-medium truncate"
            data-testid={`${testId}-label`}
          >
            {title ? (
              <>
                <span className="text-[var(--studio-muted)] font-normal">
                  {title}{' '}
                </span>
                {playlistMode
                  ? `${playlistIndex + 1}/${playlist!.length} · ${active.label}`
                  : active.label}
              </>
            ) : (
              <>
                <span className="text-[var(--studio-muted)] font-normal">
                  Now previewing:{' '}
                </span>
                {playlistMode
                  ? `${playlistIndex + 1}/${playlist!.length} · ${active.label}`
                  : active.label}
              </>
            )}
          </p>
          <p
            className="text-xs text-[var(--studio-muted)] shrink-0"
            data-testid={`${testId}-bounds`}
          >
            {formatSec(active.startSec)} → {formatSec(active.endSec)} ·{' '}
            {duration.toFixed(1)}s
          </p>
        </div>
      ) : showIdleHint ? (
        <p className="text-sm text-center text-[var(--studio-muted)] px-3 pt-3">
          {STUDIO_COPY.customizeClipPlayHint}
        </p>
      ) : null}
      {subtitle ? (
        <p
          className="text-xs text-[var(--studio-muted)] px-3"
          data-testid={`${testId}-subtitle`}
        >
          {subtitle}
        </p>
      ) : null}
      <div className="studio-customize-player-media">
        <video
          ref={videoRef}
          className="studio-customize-player-video"
          controls
          playsInline
          preload="metadata"
          src={sourceUrl ?? undefined}
          data-testid={`${testId}-video`}
          onPlay={() => {
            if (musicActive) syncMusicPlayback(true);
          }}
          onPause={() => {
            // Seeking/advance briefly pauses; only stop the bed when truly idle.
            requestAnimationFrame(() => {
              const video = videoRef.current;
              if (video && video.paused && !video.seeking) {
                syncMusicPlayback(false);
              }
            });
          }}
        />
        {musicUrl ? (
          <audio
            ref={musicRef}
            src={musicUrl}
            loop={musicLoop}
            preload="auto"
            data-testid={`${testId}-music`}
          />
        ) : null}
      </div>
      {active && buffering ? (
        <p className="text-xs text-center text-[var(--studio-muted)] pb-1">
          Buffering…
        </p>
      ) : null}
      {loadError ? (
        <p
          className="text-xs text-center text-[var(--studio-danger)] px-3 pb-2"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {loadError}
        </p>
      ) : null}
      {active ? (
        <button
          type="button"
          className="studio-btn-ghost text-xs w-full pb-2"
          onClick={() => {
            if (playlistMode) {
              if (musicUrl) restartMusic();
              playPlaylistIndex(0);
              return;
            }
            seekAndPlay(active.startSec, active.endSec);
          }}
          data-testid={`${testId}-replay`}
        >
          {playlistMode
            ? STUDIO_COPY.customizeInstantReplay
            : 'Replay clip'}
        </button>
      ) : null}
    </div>
  );
});

export default ClipSegmentPlayer;
